/**
 * /billing/family-balance page data layer (Plan 09).
 *
 * One bootstrap query covers the page surface (household + ledger),
 * plus four mutations the cards / sheet fire from.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useProfileStore } from '@/stores/profile'

import type { HouseholdPermission, HouseholdRole } from './co-owners'
import { apiFetch } from './http'

/** Whether a saved card is usable by one guardian or several. */
export type PaymentMethodVisibility = 'PRIVATE' | 'SHARED'

/** A guardian a card can be shared with. */
export interface ShareableGuardian {
  id: string
  firstName: string
  lastName: string
  preferredName: string | null
  avatarUri: string | null
  avatarColor: string | null
}

export interface SetCardSharingInput {
  /** Stripe `pm_…` id or the mirror row id. */
  paymentMethodRef: string
  /** Household the card belongs to — see `HouseholdScopedVariables`. */
  accountId: string
  visibility: PaymentMethodVisibility
  /** Required (non-empty) when SHARED. */
  sharedWithUserIds: string[]
}

export async function fetchShareableGuardians(
  accountId: string,
  signal?: AbortSignal,
): Promise<{ guardians: ShareableGuardian[] }> {
  return apiFetch<{ guardians: ShareableGuardian[] }>('/api/billing/payment-methods/shareable-with', {
    query: { accountId },
    signal,
  })
}

export async function setCardSharing(input: SetCardSharingInput): Promise<{ visibility: PaymentMethodVisibility }> {
  const { paymentMethodRef, ...body } = input
  // `body` keeps accountId — the server needs it to pick the household.
  return apiFetch<{ visibility: PaymentMethodVisibility }>(
    `/api/billing/payment-methods/${encodeURIComponent(paymentMethodRef)}/sharing`,
    { method: 'PATCH', body },
  )
}

export const shareableGuardiansQueryKey = (accountId: string) =>
  ['billing', 'payment-methods', 'shareable-with', accountId] as const

export function useShareableGuardiansQuery(accountId: string, enabled = true) {
  return useQuery<{ guardians: ShareableGuardian[] }>({
    queryKey: shareableGuardiansQueryKey(accountId),
    queryFn: ({ signal }) => fetchShareableGuardians(accountId, signal),
    enabled,
    staleTime: 60_000,
  })
}

export function useSetCardSharingMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SetCardSharingInput) => setCardSharing(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Types (kept identical to the previous GraphQL surface)
// ---------------------------------------------------------------------------

export type LedgerEntryKind =
  | 'TOPUP'
  | 'CHARGE'
  | 'REFUND'
  | 'ADJUSTMENT'
  // Stripe-side clawbacks: money that left Stripe (card refund /
  // chargeback) debiting the wallet, plus their reversals.
  | 'TOPUP_REVERSAL'
  | 'TOPUP_REVERSAL_VOID'
  | 'DISPUTE_HOLD'
  | 'DISPUTE_RELEASE'
  // An optimistic ACH top-up whose bank transfer never cleared.
  | 'TOPUP_CLAWBACK'

export interface BillingLedgerEntry {
  id: string
  kind: LedgerEntryKind
  amountCents: number
  balanceAfter: number
  currency: string
  orderBatchId: string | null
  stripeIntentId: string | null
  /**
   * Bank (ACH) top-ups are credited as soon as Stripe accepts the debit, so a
   * TOPUP can be spendable while the transfer is still clearing. Null on card
   * top-ups and every other kind.
   */
  settlementState?: 'PENDING_CLEARANCE' | 'CLEARED' | 'REVERSED' | null
  description: string | null
  occurredAt: string
  occurredByUserId: string | null
}

export interface BillingLedgerEdge {
  cursor: string
  node: BillingLedgerEntry
}

export interface BillingLedgerPageInfo {
  hasNextPage: boolean
  hasPreviousPage: boolean
  startCursor: string | null
  endCursor: string | null
}

export interface BillingLedgerConnection {
  edges: BillingLedgerEdge[]
  pageInfo: BillingLedgerPageInfo
  totalCount: number | null
}

export interface BillingPaymentMethod {
  id: string
  stripePaymentMethodId: string
  type: string
  walletType: string | null
  brand: string | null
  last4: string | null
  expMonth: number | null
  expYear: number | null
  isDefault: boolean
  /** PRIVATE = only whoever added it; SHARED = the guardians named below. */
  visibility: PaymentMethodVisibility
  /** Null on legacy cards saved before sharing existed (household-wide). */
  addedByUserId: string | null
  /** True when the viewer added this card — only they may edit or remove it. */
  isMine: boolean
  sharedWithUserIds: string[]
}

export interface BillingFamilyAccount {
  id: string
  kind: 'HOUSEHOLD' | 'ORGANIZATION'
  displayName: string | null
  /**
   * The students this wallet covers, and the campuses they attend.
   *
   * `displayName` alone cannot identify a household — it derives from adult
   * surnames, so a guardian on two households is served the same name twice —
   * so these are what the balance card and top-up sheet label the wallet with.
   * See `householdCoverageLine` in components/billing/helpers.
   */
  studentNames: string[]
  schoolNames: string[]
  /** False for a household the viewer co-owns rather than owns. */
  isOwner: boolean
  /** The viewer's role on this household; null when access is org-derived. */
  viewerRole: HouseholdRole | null
  /**
   * The viewer's effective grants on this household. Drives which actions
   * render beside the balance: TOP_UP for add-funds/auto-reload,
   * MANAGE_PAYMENT_METHODS for the cards section. Every role holds SEE_BALANCE,
   * so the balance itself always renders.
   */
  permissions: HouseholdPermission[]
  /**
   * Legacy alias kept for wire compatibility — now simply "has access", since
   * every household role holds PLACE_ORDERS. Prefer `permissions`.
   */
  canCharge: boolean
  currency: string
  /**
   * How this household settles. PREPAID = paid out of `balanceCents` (the
   * schema default — every household today); INVOICED = billed by invoice.
   * Only PREPAID households are subject to the auto-reload requirement for
   * auto-orders — see `accountPaysFromBalance` in `components/billing/helpers`.
   * Null = provisioning decision not yet made; treated as balance-funded.
   */
  billingMode: 'PREPAID' | 'INVOICED' | null
  balanceCents: number
  /** False when no current campus supports family orders or register purchases. */
  fundingEnabled?: boolean
  autoReload: boolean
  autoReloadAmountCents: number
  autoReloadThresholdCents: number | null
  /**
   * The minimum load-up this household may put on the wallet, in cents,
   * already RESOLVED by the server (PROD-1317): the configured floor on the
   * organization its students attend (`Organization.walletMinTopUpCents`),
   * the STRICTEST floor when they span several orgs, and the platform
   * default when no org resolves at all — so a household with no school
   * still gets a usable number rather than an error. Never absent: every
   * amount surface reads it from here instead of re-deriving a floor of its
   * own, which is how the client and the server's validator stay in step.
   */
  minTopUpCents: number
  defaultPaymentMethodRef: string | null
  paymentMethods: BillingPaymentMethod[]
  isArchived: boolean
  ledger: BillingLedgerConnection
}

export interface BillingFamilyResponse {
  me: {
    id: string
    firstName: string
    lastName: string
    preferredName: string | null
    ownedAccounts: BillingFamilyAccount[]
  } | null
}

export interface BillingFamilyVariables {
  first?: number
}

/**
 * Every billing control is rendered per-household, so every write must name
 * the household it was rendered for. Omitting it makes the server 409
 * (`household_ambiguous`) for a guardian on more than one household rather
 * than silently writing the oldest one (PROD-1314).
 */
export interface HouseholdScopedVariables {
  accountId: string
}

export interface SetAutoReloadVariables extends HouseholdScopedVariables {
  enabled: boolean
  thresholdCents?: number | null
  reloadAmountCents?: number | null
  /**
   * Confirms the cascade when disabling: "yes, also pause the auto-orders that
   * are charged to this balance". Without it the server 409s with those
   * auto-orders named — see `lib/auto-reload-disable.ts` for the two-step flow.
   * Ignored when enabling.
   */
  pauseAffectedSubscriptions?: boolean
}

export interface SetAutoReloadResponse {
  id: string
  autoReload: boolean
  autoReloadAmountCents: number
  autoReloadThresholdCents: number | null
  defaultPaymentMethodRef: string | null
  /** Auto-orders this call paused. Empty unless the cascade ran. */
  pausedSubscriptionIds?: string[]
}

export interface CreateTopUpIntentVariables {
  amountCents: number
  /**
   * Household to credit. Required in practice for guardians on more than one
   * household — the server 409s (`household_ambiguous`) rather than silently
   * picking the oldest one.
   */
  accountId?: string
  /**
   * Saved card to charge. Ordo picks it (not Stripe's Element) so a PRIVATE
   * card can actually stay private; the server re-checks permission. Omit to
   * collect a brand-new card in the Element.
   */
  paymentMethodId?: string
}

export interface CreatePaymentMethodVariables extends HouseholdScopedVariables {
  brand: string
  last4: string
  expMonth: number
  expYear: number
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchBillingFamily(
  vars: BillingFamilyVariables = {},
  signal?: AbortSignal,
): Promise<BillingFamilyResponse> {
  return apiFetch<BillingFamilyResponse>('/api/billing/family', {
    query: { first: vars.first ?? undefined },
    signal,
  })
}

export async function setAutoReload(vars: SetAutoReloadVariables): Promise<SetAutoReloadResponse> {
  return apiFetch<SetAutoReloadResponse>('/api/billing/auto-reload', {
    method: 'PATCH',
    body: vars,
  })
}

export interface StripeIntentResult {
  clientSecret: string | null
  /** Lets the Payment Element list the customer's saved payment methods. */
  customerSessionClientSecret?: string | null
  paymentIntentId?: string
  status?: string
  publishableKey?: string | null
}

export interface BillingConfig {
  stripePublishableKey: string | null
}

export async function fetchBillingConfig(signal?: AbortSignal): Promise<BillingConfig> {
  return apiFetch<BillingConfig>('/api/billing/config', { signal })
}

export async function createTopUpIntent(vars: CreateTopUpIntentVariables): Promise<StripeIntentResult> {
  return apiFetch<StripeIntentResult>('/api/billing/top-up-intent', {
    method: 'POST',
    body: vars,
  })
}

export interface TestTopUpResult {
  accountId: string
  balanceCents: number
}

/** Local-dev only (Stripe unconfigured): credits the wallet directly. */
export async function createTestTopUp(vars: CreateTopUpIntentVariables): Promise<TestTopUpResult> {
  return apiFetch<TestTopUpResult>('/api/billing/top-up/test', {
    method: 'POST',
    body: vars,
  })
}

export async function createSetupIntent(vars: HouseholdScopedVariables): Promise<StripeIntentResult> {
  return apiFetch<StripeIntentResult>('/api/billing/setup-intent', {
    method: 'POST',
    body: vars,
  })
}

export async function createPaymentMethod(vars: CreatePaymentMethodVariables): Promise<BillingPaymentMethod> {
  return apiFetch<BillingPaymentMethod>('/api/billing/payment-methods/test', {
    method: 'POST',
    body: vars,
  })
}

export async function setDefaultPaymentMethod(
  vars: HouseholdScopedVariables & { paymentMethodId: string },
): Promise<BillingPaymentMethod> {
  return apiFetch<BillingPaymentMethod>(
    `/api/billing/payment-methods/${encodeURIComponent(vars.paymentMethodId)}/default`,
    { method: 'PATCH', body: { accountId: vars.accountId } },
  )
}

export async function removePaymentMethod(vars: HouseholdScopedVariables & { paymentMethodId: string }): Promise<void> {
  // DELETE carries no body, so the household rides on the query string —
  // `requestedAccountId` on the server reads either spelling.
  await apiFetch<void>(`/api/billing/payment-methods/${encodeURIComponent(vars.paymentMethodId)}`, {
    method: 'DELETE',
    query: { accountId: vars.accountId },
  })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const billingFamilyQueryKey = (vars: BillingFamilyVariables) =>
  ['billing', 'family', { first: vars.first ?? null }] as const

export function useBillingFamilyQuery(vars: BillingFamilyVariables = { first: 200 }, options?: { enabled?: boolean }) {
  return useQuery<BillingFamilyResponse>({
    queryKey: billingFamilyQueryKey(vars),
    queryFn: ({ signal }) => fetchBillingFamily(vars, signal),
    enabled: options?.enabled ?? true,
  })
}

export function useBillingConfigQuery() {
  return useQuery<BillingConfig>({
    queryKey: ['billing', 'config'],
    queryFn: ({ signal }) => fetchBillingConfig(signal),
    staleTime: Infinity,
  })
}

export function useSetAutoReloadMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: setAutoReload,
    onSuccess: () => {
      void useProfileStore.getState().refresh()
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
      // `GET /api/family-subscriptions` carries the server-derived auto-reload
      // alerts, so arming auto-reload changes that payload too. Without this,
      // the Home / My Family / Billing warnings would keep claiming an
      // auto-order can't be funded after the parent just fixed it.
      //
      // The DISABLE direction needs it just as much: the confirmed cascade
      // moves subscriptions to PAUSED in the same call, so the three alert
      // surfaces would keep showing stale ENABLED rows without this.
      void qc.invalidateQueries({ queryKey: ['family-subscriptions'] })
    },
  })
}

export function useCreateTopUpIntentMutation() {
  return useMutation({ mutationFn: createTopUpIntent })
}

export function useCreateTestTopUpMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createTestTopUp,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
    },
  })
}

export function useCreateSetupIntentMutation() {
  return useMutation({ mutationFn: createSetupIntent })
}

export function useCreatePaymentMethodMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createPaymentMethod,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
    },
  })
}

export function useSetDefaultPaymentMethodMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: setDefaultPaymentMethod,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
    },
  })
}

export function useRemovePaymentMethodMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: removePaymentMethod,
    onSuccess: () => {
      void useProfileStore.getState().refresh()
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
    },
  })
}
